import React, { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector, useWidgetLocalParams, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { useWidgetId } from '@src/core/IdProvider';
import { addCartItemAddedSelector, openCartModal } from '@src/features/cart/store/cart';
import { resetAdderState, closeAdderModal } from '@src/stores/product';
import { closeMultiProductBookingModal, multiProductBookingSelector } from '@src/stores/multiProductBooking';
import {
  loadProductUpsellOffers,
  openProductUpsellModal,
  productUpsellOffersStatusSelector,
} from '@src/stores/productUpsell';

import useSelectedProductId from '@src/hooks/useSelectedProductId';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import useFeatureFlag from '@src/hooks/useFeatureFlag';
import { useAdderError } from './useAdderError';

export const useAddToCartStatus = () => {
  const { status, widgetId: cartItemAddedWidgetId } = useAppSelector(addCartItemAddedSelector);
  const id = useWidgetId();
  const prodId = useSelectedProductId();
  const errorMessages = useAdderError();
  const dispatch = useAppDispatch();
  const localParams = useWidgetLocalParams();
  const autoCheckoutRedirect = useWidgetParam('auto-checkout-redirect');
  const openCartWhenProductAddedParameter = useWidgetParam('open-cart-when-product-added');
  const checkoutPage = useWidgetParam('checkout-page');
  const timeout = useWidgetParam('auto-checkout-redirect-timeout');
  const enableModal = useWidgetParam('enable-modal');
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const packageId = usePackageIdParam();
  const booking = useAppSelector(multiProductBookingSelector);
  const upsellOffersStatusSelector = useMemo(() => productUpsellOffersStatusSelector(prodId ?? ''), [prodId]);
  const {
    offers: upsellOffers,
    hasLoaded: hasLoadedUpsellOffers,
    isLoading: isLoadingUpsellOffers,
  } = useAppSelector(upsellOffersStatusSelector);
  const hasHandledSuccessfulAddRef = useRef(false);

  const showAsModal = enableModal && enableModal == 'true';
  const isProductUpsellEnabled = isFeatureFlagEnabled('PR-4007-upsell');

  const openCart = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();

    dispatch(openCartModal());
  };

  const tryAgain = () => dispatch(resetAdderState(id, prodId, packageId));

  useEffect(() => {
    if (!status) {
      hasHandledSuccessfulAddRef.current = false;
      return;
    }

    if (cartItemAddedWidgetId !== id || hasHandledSuccessfulAddRef.current) {
      return;
    }

    // For packages the whole booking is added at once (addPackageToCart), so let the completion
    // auto-close the modal and open the cart here — mirroring the upsell abandon and the pre-4007
    // package flow. Upsell still bails: it drives close/open through abandonMultiProductBooking.
    if (booking?.isOpen && booking.widgetId === id && !packageId) {
      return;
    }

    if (isProductUpsellEnabled && prodId && !packageId) {
      if (!isLoadingUpsellOffers && !hasLoadedUpsellOffers) {
        dispatch(loadProductUpsellOffers(prodId));
      }

      if (isLoadingUpsellOffers || !hasLoadedUpsellOffers) {
        return;
      }

      if (hasLoadedUpsellOffers && upsellOffers.length > 0) {
        dispatch(openProductUpsellModal(prodId, id, localParams));
        dispatch(closeAdderModal(id, packageId));
        hasHandledSuccessfulAddRef.current = true;
        return;
      }
    }

    if (autoCheckoutRedirect && checkoutPage) {
      setTimeout(
        () => {
          dispatch(changeLocation(checkoutPage));
        },
        timeout ? +timeout : 3000,
      );
    }

    if (showAsModal || openCartWhenProductAddedParameter === 'true') {
      dispatch(openCartModal());
    }

    dispatch(closeAdderModal(id, packageId));
    if (packageId) {
      dispatch(closeMultiProductBookingModal());
    }
    hasHandledSuccessfulAddRef.current = true;
  }, [
    autoCheckoutRedirect,
    booking,
    cartItemAddedWidgetId,
    checkoutPage,
    dispatch,
    hasLoadedUpsellOffers,
    id,
    isLoadingUpsellOffers,
    isProductUpsellEnabled,
    localParams,
    openCartWhenProductAddedParameter,
    packageId,
    prodId,
    showAsModal,
    status,
    timeout,
    upsellOffers.length,
  ]);

  return {
    status,
    errorMessages,
    openCart,
    tryAgain,
  };
};
